import React, { ReactNode, useMemo, useRef, useState } from 'react';
import {
	NavigationStepperItemType,
	NavigationStepperRef,
} from '../../../../Components/Stepper/NavigationStepper';
import SubTabNavigationStepper from '../../../../Components/Stepper/SubTabNavigationStepper/SubTabNavigationStepper';
import MzcrPatientService, {
	MzcrPatientServiceRenderProps,
} from '../../../Mzcr/Components/MzcrPatientService';
import { usePatientTreatmentContext } from './ContextProvider';
import PatientTreatmentPageWrapper from './PatientTreatmentPageWrapper';

/** The sub-tabs of a MZČR service: a new item, the sent ones and the received ones. */
export type MzcrSubTabView = 'new' | 'sent' | 'received';

const VIEWS: MzcrSubTabView[] = ['new', 'sent', 'received'];

export interface MzcrSubTabsRenderArgs {
	view: MzcrSubTabView;
	service: MzcrPatientServiceRenderProps;
	/** Switches to another sub-tab. */
	onViewChange: (next: MzcrSubTabView) => void;
	/** Reports the count shown on the "Přijaté" tab. */
	onNewReceivedCountChange: (count: number) => void;
}

interface Props {
	/** Translation key prefix – the tab names are `<prefix>.<view>.title`. */
	translationPrefix: string;
	initialView: MzcrSubTabView;
	/** Switches the patient card to the Profile tab (missing data for the MZČR registry). */
	onOpenProfile?: () => void;
	/** Renders the MZČR service for one sub-tab, once the patient is paired with the registry. */
	renderService: (args: MzcrSubTabsRenderArgs) => ReactNode;
}

/**
 * Sub-tabs "Nová …", "Odeslané" and "Přijaté" of a MZČR service in the patient card (eŽádanky,
 * Dočasné úložiště), each showing only this patient's items.
 */
const MzcrSubTabs: React.FC<Props> = ({
	translationPrefix,
	initialView,
	onOpenProfile,
	renderService,
}) => {
	const { patientId } = usePatientTreatmentContext();
	const [newReceivedCount, setNewReceivedCount] = useState(0);
	const tabsRef = useRef<NavigationStepperRef>(null);

	const steps: NavigationStepperItemType[] = useMemo(
		() =>
			VIEWS.map(view => ({
				name: `${translationPrefix}.${view}.title`,
				key: view,
				badge: view === 'received' ? newReceivedCount : undefined,
				component: () => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<MzcrPatientService
							patientId={patientId}
							onOpenProfile={onOpenProfile}>
							{service =>
								renderService({
									view,
									service,
									onViewChange: next =>
										tabsRef.current?.setActiveStepPath(next),
									onNewReceivedCountChange: setNewReceivedCount,
								})
							}
						</MzcrPatientService>
					</PatientTreatmentPageWrapper>
				),
			})),
		[
			translationPrefix,
			patientId,
			onOpenProfile,
			newReceivedCount,
			renderService,
		],
	);

	return (
		<SubTabNavigationStepper
			ref={tabsRef}
			steps={steps}
			initialStepPath={initialView}
			canSkip={true}
		/>
	);
};

export default MzcrSubTabs;
